import type { SubscriptionPeriod, SubscriptionTier, SubscriptionType } from "../types";
import type { TeamsUserRole } from "./useAccountTypes";
import type { PaymentSource } from "./useBilling";
import type { Estimate } from "./useCheckout";
import type { TeamSpace } from "./useTeamSpaces";

export const SeatBasedTeamSubscriptionOrigins = {
  Shapr: "shapr",
  Chargebee: "chargebee",
} as const;

export type SeatBasedTeamSubscriptionOrigin =
  (typeof SeatBasedTeamSubscriptionOrigins)[keyof typeof SeatBasedTeamSubscriptionOrigins];

export type TeamMember = {
  email: string;
  role: TeamsUserRole;
  userId?: string;
  invitationId?: string;
  name?: string;
  spaces?: TeamSpace[] | undefined;
};

export type TeamsUsersResponseData = {
  users: TeamMember[];
};

export type IndividualSubscription = {
  id: number;
  paddleId?: string;
  type: SubscriptionType;
  expirationDate: string;
  autoRenewal: boolean;
  isTrial: boolean;
  subscriptionPeriod: SubscriptionPeriod;
  tier: SubscriptionTier;
  gracePeriodExpirationDate?: string;
  isAutoRenewEditable: boolean;
  isOnlyCancelable: boolean;
};

export type SeatBasedTeamSubscription = {
  id: string;
  origin: SeatBasedTeamSubscriptionOrigin;
  expirationDate: string;
  autoRenewal: boolean;
  isTrial: boolean;
  subscriptionPeriod: SubscriptionPeriod;
  tier: SubscriptionTier;
  gracePeriodExpirationDate?: string;
  editorSeats: number;
  editorSeatsUsed: number;
};

export type CreditBasedTeamSubscription = {
  id: string;
  tier: SubscriptionTier;
  periodEndDate?: string;
};

export type TeamsSubscriptionResponseData = {
  individualSubscriptions?: IndividualSubscription[];
  seatBasedTeamSubscription?: SeatBasedTeamSubscription;
  creditBasedTeamSubscription?: CreditBasedTeamSubscription;
};

export type TeamsSubscriptionsEstimateRequestPayload = {
  additionalEditorSeatCount: number | null;
  tier?: SubscriptionTier;
};

export type TeamsSubscriptionsEstimateResponseData = {
  prorated?: Estimate;
  renewal: Estimate;
  renewalDate: string;
  primaryPaymentSource: PaymentSource;
};

export type TeamsSubscriptionsEstimateSelectedData = TeamsSubscriptionsEstimateResponseData & {
  currencySymbol: string;
};

export type UpdateTeamSubscriptionRequestPayload = {
  additionalEditorSeatCount: number | null;
  tier?: SubscriptionTier;
};

export type TeamsInvitationsEstimateRequestPayload = {
  emails: string[];
  role: TeamsUserRole;
};

export type TeamInvitationSeatUpgradeEstimate = {
  prorated?: Estimate;
  renewal: Estimate;
  renewalDate: string;
  primaryPaymentSource: PaymentSource;
};

export type SalesTeamSeatUpgradeEstimate = {
  additionalEditorSeatCount: number;
};

export type TeamsInvitationsEstimateResponseData = {
  available: boolean;
  contributorBlockedEmailCount: number;
  seatUpgradeEstimate?: TeamInvitationSeatUpgradeEstimate;
  newSubscriptionEstimate?: Estimate;
  salesTeamSeatUpgradeEstimate?: SalesTeamSeatUpgradeEstimate;
  errors?: { email: string; code: string; message: string }[];
};

export type TeamsInvitationsMutationRequestPayload = {
  emails: string[];
  role: TeamsUserRole;
};

export type TeamsInvitationsMutationResponseData = {
  invitedUsers: TeamMember[];
  errors?: { email: string; code: string; message: string }[];
};
